import { Mesh, EngineStore, VertexData, VertexBuffer, Vector3, } from '@babylonjs/core';
import { PointCloudMaterial } from './material/point-cloud-material';
export class SimplePointsCloudSystem {
    /**
     * The PCS name. This name is also given to the underlying mesh.
     */
    name;
    /**
     * The PCS mesh. It's a standard BJS Mesh, so all the methods from the Mesh class are available.
     */
    mesh;
    /**
     * The PCS total number of particles. Read only. Use PCS.counter instead if you need to set your own value.
     */
    nbParticles = 0;
    /**
     * @internal
     */
    _size; // size of each point particle
    _position = new Vector3(0, 0, 0);
    _scene;
    _material;
    _positions32;
    _colors32;
    _uvs32;
    get position() {
        return this._position;
    }
    set position(value) {
        this._position = value;
        if (this.mesh) {
            this.mesh.position.copyFrom(value);
        }
    }
    /**
     * Gets the particle positions computed by the Point Cloud System
     */
    get positions() {
        return this._positions32;
    }
    /**
     * Gets the particle colors computed by the Point Cloud System
     */
    get colors() {
        return this._colors32;
    }
    /**
     * Gets the particle uvs computed by the Point Cloud System
     */
    get uvs() {
        return this._uvs32;
    }
    /**
     * Creates a PCS (Points Cloud System) object
     * @param name (String) is the PCS name, this will be the underlying mesh name
     * @param pointSize (number) is the size for each point. Has no effect on a WebGPU engine.
     * @param scene (Scene) is the scene in which the PCS is added
     */
    constructor(name, pointSize, scene, material) {
        this.name = name;
        this._size = pointSize;
        this._scene = scene || EngineStore.LastCreatedScene;
        this._material = material;
    }
    buildMeshFromBuffer(positions, colors, uvs) {
        const vertexData = new VertexData();
        if (positions && positions.length > 0) {
            this._positions32 = positions;
            // Invert Z values (multiply by -1)
            // if (positions && positions.length > 0) {
            //   // Create a copy of the positions array to avoid modifying the original
            //   const invertedPositions = new Float32Array(positions.length);
            //   for (let i = 0; i < positions.length; i += 3) {
            //     invertedPositions[i] = positions[i];       // x
            //     invertedPositions[i + 1] = positions[i + 1]; // y
            //     invertedPositions[i + 2] = -positions[i + 2]; // z (inverted)
            //   }
            //   this._positions32 = invertedPositions;
            // }
            vertexData.set(this._positions32, VertexBuffer.PositionKind);
        }
        if (uvs && uvs.length > 0) {
            this._uvs32 = uvs;
            vertexData.set(this._uvs32, VertexBuffer.UVKind);
        }
        if (colors && colors.length > 0) {
            this._colors32 = colors;
            vertexData.set(this._colors32, VertexBuffer.ColorKind);
        }
        const mesh = new Mesh(this.name, this._scene);
        vertexData.applyToMesh(mesh, false);
        this.mesh = mesh;
        this.mesh.isPickable = false;
        if (this.position) {
            this.mesh.position.copyFrom(this.position);
        }
        let mat = this._material;
        if (!mat) {
            mat = new PointCloudMaterial(this.name, this._scene);
        }
        this.mesh.material = mat;
    }
    buildMeshFromGeometry(geometry) {
        const positions = geometry?.getVerticesData(VertexBuffer.PositionKind);
        const colors = geometry?.getVerticesData(VertexBuffer.ColorKind);
        const uvs = geometry?.getVerticesData(VertexBuffer.UVKind);
        this.buildMeshFromBuffer(positions, colors, uvs);
    }
    dispose() {
        this.mesh?.dispose();
        this._positions32 = null;
        this._colors32 = null;
        this._uvs32 = null;
    }
}
